import { SalesOrder, SalesOrderItem } from 'services/salesOrders';
import { CustomField, CustomFieldValue } from 'services/customFields/types';
import {
  LabelElement,
  isTextElement,
  isBarcodeElement,
  isQRCodeElement,
  QRCodeElementProps,
} from './types';

export const MONEY_FIELDS = [
  'amount',
  'price',
  'total',
  'subtotal',
  'tax',
  'discount',
  'unitPrice',
  'lineTotal',
  'balance',
  'payment',
  'charge',
  'fee',
  'adjustment',
  'grandTotal',
];

export const formatCurrency = (value: any): string => {
  const num = Number.parseFloat(String(value));
  if (
    isNaN(num) ||
    value === null ||
    value === undefined ||
    String(value).trim() === ''
  ) {
    return '$0.00';
  }
  return `$${num.toFixed(2)}`;
};

// Assuming CustomField and CustomFieldValue types are imported or available in this scope
// import { CustomField, CustomFieldValue } from './types'; // Example import

const resolveCustomFieldValueFromObject = (
  dataObject: SalesOrder | SalesOrderItem,
  fullPath: string // e.g., "customFields.ActualCustomFieldName"
): any | undefined => {
  // 'any' for flexibility, as CustomFieldValue can be varied
  if (
    !dataObject ||
    typeof dataObject !== 'object' ||
    !('customFields' in dataObject) ||
    !Array.isArray((dataObject as any).customFields)
  ) {
    return undefined;
  }

  const customFieldName = fullPath.split('customFields.')[1];
  if (!customFieldName) {
    return undefined; // Malformed path for custom field
  }

  const customFieldsArray = (dataObject as any).customFields as CustomField[];
  const customField = customFieldsArray.find(
    (cf) => cf && cf.name === customFieldName
  );

  if (
    customField &&
    customField.customFieldValues !== undefined &&
    customField.customFieldValues !== null
  ) {
    return customField.customFieldValues;
  }

  return undefined;
};

export const getSOValueAtPath = (
  dataObject: SalesOrder | SalesOrderItem,
  path: string
): string | undefined => {
  // If the path is exactly 'customFields', it refers to the array of custom field definitions,
  // not a singular displayable value. The expectation is to return undefined for such a path.
  if (path === 'customFields') {
    return undefined;
  }

  let resolvedValue: any = path
    .split('.')
    .reduce((acc: any, currentKey: string) => {
      if (acc && typeof acc === 'object' && currentKey in acc) {
        return acc[currentKey];
      }
      return undefined;
    }, dataObject);

  if (resolvedValue === undefined && path.includes('customFields.')) {
    resolvedValue = resolveCustomFieldValueFromObject(dataObject, path);
  }

  if (resolvedValue !== undefined && resolvedValue !== null) {
    const formatValueToString = (val: CustomFieldValue | any): string => {
      if (Array.isArray(val)) {
        return val.map(String).join(', ');
      }
      if (val instanceof Date) {
        return val.toLocaleDateString();
      }
      if (typeof val === 'boolean') {
        return String(val);
      }
      return String(val);
    };
    return formatValueToString(resolvedValue);
  }

  return undefined;
};

export const getResolvedDisplayValue = (
  element: LabelElement,
  previewMode: boolean,
  previewData: SalesOrder | null
): string => {
  const fieldKey = element.dynamicField;

  if (previewMode && fieldKey && previewData) {
    const value = getSOValueAtPath(previewData, fieldKey);
    if (value === null || value === undefined || String(value).trim() === '') {
      return '';
    }
    return String(value);
  }

  if (fieldKey) {
    return `{${fieldKey}}`;
  }

  if (isTextElement(element)) {
    return element.text || 'Text';
  }
  if (isBarcodeElement(element)) {
    return element.barcodeValue || 'Sample';
  }
  if (isQRCodeElement(element)) {
    return (element as QRCodeElementProps).qrValue || 'Sample QR Code';
  }
  return 'Text';
};
